import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import api from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushToggle() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, []);

  async function enable() {
    setBusy(true);
    try {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      if (!data.public_key) { alert("Push not configured on server"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys });
      setSubscribed(true);
    } catch (err) {
      console.error(err);
      alert("Could not enable notifications");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      await api.post("/push/test");
    } finally {
      setTesting(false);
    }
  }

  if (!supported) return null;

  if (permission === "denied") {
    return (
      <div className="inline-flex items-center gap-2 border-2 border-slate-300 bg-slate-50 px-3 py-2 industrial-label text-slate-500" data-testid="push-denied">
        <BellOff className="h-4 w-4" /> Notifications blocked in browser
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      {subscribed ? (
        <>
          <button
            type="button"
            onClick={test}
            disabled={testing}
            data-testid="push-test-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-60"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
            Test
          </button>
          <button
            type="button"
            onClick={disable}
            disabled={busy}
            data-testid="push-disable-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-slate-900 px-3 py-2 industrial-label text-white transition-all hover:-translate-y-0.5 hover:bg-safety-500 hover:text-slate-900 hover:shadow-brutal-sm disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
            Disable Push
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          data-testid="push-enable-btn"
          className="inline-flex items-center gap-2 border-2 border-slate-900 bg-safety-500 px-3 py-2 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          Enable Push Alerts
        </button>
      )}
    </div>
  );
}
